import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../../../hooks/useApi";
import { useAuth } from "../../../../contexts/AuthContext";

// Shared HRMS server-state layer. Every page talks to /api/v1/hrms/* through
// these two hooks — no page owns its own axios plumbing.

const BASE = "/api/v1/hrms";

// The API's failure envelope is { success: false, error: { message, details } }.
// Axios's own e.message is "Request failed with status code 400", which tells the
// user nothing — the server already explained itself ("Selected range has no
// working days"), the message was just being read from the wrong place. Normalize
// once here so every caller that surfaces err.message shows the real reason.
export function hrmsErrorMessage(e) {
  const d = e?.response?.data;
  return d?.error?.message || d?.message || e?.message || "Something went wrong";
}

const rethrow = (e) => {
  const err = new Error(hrmsErrorMessage(e));
  err.status = e?.response?.status ?? null;
  err.details = e?.response?.data?.error?.details || [];
  err.cause = e;
  throw err;
};

export function useHrms() {
  const api = useApi();
  const { user } = useAuth();
  const isHr = ["org_admin", "main_admin"].includes(user?.role);

  const get = useCallback(async (path) => {
    try { return (await api.get(`${BASE}${path}`)).data?.data; } catch (e) { return rethrow(e); }
  }, [api]);
  const post = useCallback(async (path, body) => {
    try { return (await api.post(`${BASE}${path}`, body)).data?.data; } catch (e) { return rethrow(e); }
  }, [api]);
  const patch = useCallback(async (path, body) => {
    try { return (await api.patch(`${BASE}${path}`, body)).data?.data; } catch (e) { return rethrow(e); }
  }, [api]);
  const put = useCallback(async (path, body) => {
    try { return (await api.put(`${BASE}${path}`, body)).data?.data; } catch (e) { return rethrow(e); }
  }, [api]);
  const del = useCallback(async (path) => {
    try { return (await api.delete(`${BASE}${path}`)).data?.data; } catch (e) { return rethrow(e); }
  }, [api]);
  // Multipart post — fields is a plain object; file goes under the "file" key.
  const postForm = useCallback(async (path, fields, file) => {
    const fd = new FormData();
    for (const [k, v] of Object.entries(fields || {})) {
      if (v !== undefined && v !== null && v !== "") fd.append(k, v);
    }
    if (file) fd.append("file", file);
    try {
      const res = await api.post(`${BASE}${path}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      return res.data?.data;
    } catch (e) { return rethrow(e); }
  }, [api]);

  return { user, isHr, get, post, patch, put, del, postForm };
}

// Declarative fetch with refresh — `useFetch("/employees")`.
export function useFetch(path, deps = []) {
  const { get } = useHrms();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!path) { setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    get(path)
      .then((d) => { if (alive) { setData(d); setError(null); } })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, tick, ...deps]);

  const refresh = useCallback(() => setTick((x) => x + 1), []);
  return { data, loading, error, refresh };
}

export const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";
export const fmtDateShort = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—";
export const fmtMoney = (n) =>
  n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN")}`;
export const fullName = (u) => (u ? `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "—" : "—");
export const monthKey = (d = new Date()) => d.toISOString().slice(0, 7);

// Bounds for date/month inputs, mirroring the server's windows so the browser
// refuses out-of-range values before a request is ever made. A bare
// <input type="month"> lets the year spinner run to 275760, which is how a
// payroll run for "2883-02" got created.
const shiftMonths = (n) => {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + n);
  return monthKey(d);
};
const shiftYears = (n) => {
  const d = new Date();
  d.setUTCFullYear(d.getUTCFullYear() + n);
  return d.toISOString().slice(0, 10);
};
export const CYCLE_MONTH_BOUNDS = { min: shiftMonths(-120), max: shiftMonths(12) };
export const JOINED_AT_BOUNDS = { min: shiftYears(-60), max: shiftYears(1) };
